import { motion } from 'framer-motion';

const GraphVisualization = ({ step, lessonId }) => {
  if (!step || !step.graph) return null;

  const { graph, visited = [], queue = [], stack = [], current, exploredEdge, distances, dataStructure } = step;
  const visitedSet = new Set(visited);

  const isEdgeExplored = (a, b) => {
    if (!exploredEdge) return false;
    return (exploredEdge[0] === a && exploredEdge[1] === b) ||
           (exploredEdge[0] === b && exploredEdge[1] === a);
  };

  const isEdgeInTraversal = (a, b) => {
    return visitedSet.has(a) && visitedSet.has(b);
  };

  return (
    <div className="space-y-4">
      {/* Graph SVG */}
      <div className="bg-[#0F0F12] border border-white/10 rounded-lg p-3 flex justify-center">
        <svg width="600" height="410" viewBox="0 0 600 410" className="max-w-full">
          {/* Edges */}
          {graph.edges.map(([a, b], idx) => {
            const nodeA = graph.nodes.find(n => n.id === a);
            const nodeB = graph.nodes.find(n => n.id === b);
            const explored = isEdgeExplored(a, b);
            const traversed = isEdgeInTraversal(a, b);
            const weight = graph.weights?.[`${Math.min(a, b)}-${Math.max(a, b)}`];

            const midX = (nodeA.x + nodeB.x) / 2;
            const midY = (nodeA.y + nodeB.y) / 2;

            return (
              <g key={idx}>
                <motion.line
                  x1={nodeA.x}
                  y1={nodeA.y}
                  x2={nodeB.x}
                  y2={nodeB.y}
                  stroke={explored ? '#FFB800' : traversed ? '#007AFF' : '#4A4A50'}
                  strokeWidth={explored ? 3 : traversed ? 2.5 : 1.5}
                  animate={{ 
                    stroke: explored ? '#FFB800' : traversed ? '#007AFF' : '#4A4A50',
                    strokeWidth: explored ? 3 : traversed ? 2.5 : 1.5
                  }}
                  transition={{ duration: 0.3 }}
                />
                {lessonId === 'dijkstra' && weight !== undefined && (
                  <g>
                    <circle cx={midX} cy={midY} r="12" fill="#0F0F12" stroke="#71717A" strokeWidth="1" />
                    <text
                      x={midX}
                      y={midY + 4}
                      textAnchor="middle"
                      fill="#FFB800"
                      fontSize="11"
                      fontFamily="JetBrains Mono, monospace"
                      fontWeight="bold"
                    >
                      {weight}
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Nodes */}
          {graph.nodes.map(node => {
            const isVisited = visitedSet.has(node.id);
            const isCurrent = current === node.id;
            const nodeDist = distances?.[node.id];

            let fillColor = '#1A1A20';
            let strokeColor = '#71717A';
            let textColor = 'white';

            if (isCurrent) {
              fillColor = '#FFB800';
              strokeColor = '#FFC520';
              textColor = 'black';
            } else if (isVisited) {
              fillColor = '#007AFF';
              strokeColor = '#00D4FF';
              textColor = 'white';
            }

            return (
              <g key={node.id}>
                <motion.circle
                  cx={node.x}
                  cy={node.y}
                  r={26}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={isCurrent ? 3 : 2}
                  animate={{
                    fill: fillColor,
                    stroke: strokeColor,
                    r: isCurrent ? 28 : 26
                  }}
                  transition={{ duration: 0.3 }}
                  filter={isCurrent ? 'drop-shadow(0 0 12px rgba(255, 184, 0, 0.7))' : isVisited ? 'drop-shadow(0 0 8px rgba(0, 122, 255, 0.5))' : 'none'}
                  data-testid={`graph-node-${node.id}`}
                />
                <text
                  x={node.x}
                  y={node.y + 5}
                  textAnchor="middle"
                  fill={textColor}
                  fontSize="15"
                  fontFamily="JetBrains Mono, monospace"
                  fontWeight="bold"
                >
                  {node.id}
                </text>
                {/* Distance label for Dijkstra */}
                {lessonId === 'dijkstra' && nodeDist !== undefined && (
                  <text
                    x={node.x}
                    y={node.y - 34}
                    textAnchor="middle"
                    fill="#FFB800"
                    fontSize="11"
                    fontFamily="JetBrains Mono, monospace"
                    fontWeight="bold"
                  >
                    d={nodeDist === Infinity ? '∞' : nodeDist}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Data Structure Panel */}
      <div className="grid grid-cols-2 gap-3">
        {dataStructure === 'queue' && (
          <div className="bg-[#0F0F12] border border-[#FFB800]/20 rounded p-3">
            <div className="text-[#FFB800] text-xs font-code mb-2">QUEUE (FIFO)</div>
            <div className="flex gap-1 flex-wrap">
              {queue.length === 0 ? (
                <span className="text-[#71717A] text-xs font-code italic">empty</span>
              ) : queue.map((n, i) => (
                <div key={i} className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded px-2 py-1 text-xs font-code text-[#FFB800]">
                  {n}
                </div>
              ))}
            </div>
          </div>
        )}
        {dataStructure === 'stack' && (
          <div className="bg-[#0F0F12] border border-[#FFB800]/20 rounded p-3">
            <div className="text-[#FFB800] text-xs font-code mb-2">STACK (LIFO)</div>
            <div className="flex gap-1 flex-wrap">
              {stack.length === 0 ? (
                <span className="text-[#71717A] text-xs font-code italic">empty</span>
              ) : stack.map((n, i) => (
                <div key={i} className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded px-2 py-1 text-xs font-code text-[#FFB800]">
                  {n}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="bg-[#0F0F12] border border-green-500/20 rounded p-3">
          <div className="text-green-400 text-xs font-code mb-2">VISITED ({visited.length})</div>
          <div className="flex gap-1 flex-wrap">
            {visited.length === 0 ? (
              <span className="text-[#71717A] text-xs font-code italic">none</span>
            ) : visited.map((n, i) => (
              <div key={i} className="bg-green-500/10 border border-green-500/30 rounded px-2 py-1 text-xs font-code text-green-400">
                {n}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Traversal Order */}
      {step.traversal && (
        <div className="bg-[#0F0F12] border border-[#007AFF]/20 rounded p-3">
          <div className="text-[#007AFF] text-xs font-code mb-1">TRAVERSAL ORDER</div>
          <div className="font-code text-white text-sm">
            {step.traversal.length === 0 ? '(none)' : step.traversal.join(' → ')}
          </div>
        </div>
      )}

      {/* Result */}
      {step.result !== undefined && (
        <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">
            {lessonId === 'dijkstra' ? 'Shortest Distances' : 'Traversal Result'}
          </div>
          <div className="text-green-400 text-base font-code font-bold break-words">{step.result}</div>
        </div>
      )}
    </div>
  );
};

export default GraphVisualization;
